/**
 * Customer Portal (UI) - billing dashboard route.
 */
import { useEffect, useMemo, useState } from 'react';
import { ScopeControls } from '../components/layout/ScopeControls';
import { fetchBilling, fetchBrands, fetchCampaigns, fetchPhoneNumbers } from '../features/api/client';
import type { AccountContextState } from '../features/account-context/account-context-store';
import type { FilterStore } from '../features/filters/filter-store';
import type { BillingResponse, Brand, Campaign, PhoneNumber } from '../types/portal';
import { dateInputToIsoBoundary, getBrowserTimeZone, toLocalDateInputValue } from '../utils/datetime';

type BillingRouteProps = {
  accountContext: AccountContextState;
  filters: FilterStore;
};

function formatMicros(value: number, currency = 'USD'): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 4
  }).format(value / 1_000_000);
}

export function BillingRoute({ accountContext, filters }: BillingRouteProps) {
  const browserTimeZone = useMemo(() => getBrowserTimeZone(), []);
  const [brands, setBrands] = useState<Brand[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneNumber[]>([]);
  const [billing, setBilling] = useState<BillingResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [startDate, setStartDate] = useState<string>(toLocalDateInputValue(new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)));
  const [endDate, setEndDate] = useState<string>(toLocalDateInputValue(new Date()));

  const range = useMemo(
    () => ({
      startIso: dateInputToIsoBoundary(startDate, 'start'),
      endIso: dateInputToIsoBoundary(endDate, 'end'),
      timeZone: browserTimeZone
    }),
    [browserTimeZone, endDate, startDate]
  );

  useEffect(() => {
    const accountId = accountContext.selectedAccountId;
    if (!accountId) return;
    let cancelled = false;
    setLoading(true);
    Promise.all([
      fetchBrands(accountId),
      fetchCampaigns(accountId),
      fetchPhoneNumbers(accountId, filters.filters),
      fetchBilling(accountId, filters.filters, range)
    ])
      .then(([brandPayload, campaignPayload, phonePayload, billingPayload]) => {
        if (cancelled) return;
        setBrands(brandPayload.items);
        setCampaigns(campaignPayload.items);
        setPhoneNumbers(phonePayload.items);
        setBilling(billingPayload.billing);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : String(err));
      })
      .finally(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [accountContext.selectedAccountId, filters.filters, range]);

  if (accountContext.loading) return <div className="alert alert-info">Loading account context...</div>;
  if (accountContext.error) return <div className="alert alert-danger">{accountContext.error}</div>;
  if (!accountContext.selectedAccountId) return <div className="alert alert-warning">No account selected.</div>;

  const scopedCampaigns = filters.filters.brandId
    ? campaigns.filter((campaign) => campaign.brandId === filters.filters.brandId)
    : campaigns;
  const hasBillingShape = Boolean(
    billing &&
      billing.range &&
      billing.summary &&
      Array.isArray(billing.pricing)
  );
  const currency = billing?.pricing[0]?.currency ?? 'USD';
  const templateRows = (billing?.summary.templateBreakdown ?? []).filter((row) => row.templateId).slice(0, 15);
  const statusRows = billing?.summary.statusBreakdown ?? [];

  return (
    <>
      <div className="page-pretitle">Usage and cost</div>
      <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-3">
        <h2 className="page-title mb-0">Billing</h2>
        <div className="d-flex align-items-center gap-2">
          <label className="form-label mb-0">
            Start
            <input className="form-control form-control-sm" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
          </label>
          <label className="form-label mb-0">
            End
            <input className="form-control form-control-sm" type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </label>
        </div>
      </div>

      <ScopeControls
        accounts={accountContext.accounts}
        selectedAccountId={accountContext.selectedAccountId}
        canSwitchAccounts={accountContext.canSwitchAccounts}
        brands={brands}
        campaigns={scopedCampaigns}
        phoneNumbers={phoneNumbers}
        filters={filters.filters}
        onSelectAccount={(accountId) => {
          accountContext.setSelectedAccountId(accountId);
          filters.resetFilters();
        }}
        onSelectBrand={(brandId) => filters.setBrandId(brandId)}
        onSelectCampaign={(campaignId) => {
          filters.setCampaignId(campaignId);
          filters.setPhoneNumberId(null);
        }}
        onSelectPhoneNumber={(phoneNumberId) => filters.setPhoneNumberId(phoneNumberId)}
        onResetFilters={filters.resetFilters}
      />

      {loading ? <div className="alert alert-info">Loading billing data...</div> : null}
      {error ? <div className="alert alert-danger">{error}</div> : null}

      {!loading && !error && billing && hasBillingShape ? (
        <>
          <div className="row row-cards mb-3">
            <div className="col-sm-6 col-lg-3">
              <div className="card">
                <div className="card-body">
                  <div className="text-secondary">Total cost</div>
                  <div className="h2 mb-0">{formatMicros(billing.summary.costMicros, currency)}</div>
                </div>
              </div>
            </div>
            <div className="col-sm-6 col-lg-3">
              <div className="card">
                <div className="card-body">
                  <div className="text-secondary">Total messages</div>
                  <div className="h2 mb-0">{billing.summary.messageCount.toLocaleString()}</div>
                </div>
              </div>
            </div>
            <div className="col-sm-6 col-lg-3">
              <div className="card">
                <div className="card-body">
                  <div className="text-secondary">Total segments</div>
                  <div className="h2 mb-0">{billing.summary.totalSegments.toLocaleString()}</div>
                </div>
              </div>
            </div>
            <div className="col-sm-6 col-lg-3">
              <div className="card">
                <div className="card-body">
                  <div className="text-secondary">Deliverability</div>
                  <div className="h2 mb-0">{billing.summary.deliverability.ratePct}%</div>
                  <div className="small text-secondary">delivered / successfully sent upstream</div>
                </div>
              </div>
            </div>
          </div>

          <div className="row row-cards mb-3">
            <div className="col-lg-6">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Direction breakdown</h3>
                </div>
                <div className="table-responsive">
                  <table className="table table-vcenter card-table">
                    <thead>
                      <tr>
                        <th>Direction</th>
                        <th>Messages</th>
                        <th>Segments</th>
                        <th>Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Outbound</td>
                        <td>{billing.summary.outbound.messageCount.toLocaleString()}</td>
                        <td>{billing.summary.outbound.totalSegments.toLocaleString()}</td>
                        <td>{formatMicros(billing.summary.outbound.costMicros, currency)}</td>
                      </tr>
                      <tr>
                        <td>Inbound</td>
                        <td>{billing.summary.inbound.messageCount.toLocaleString()}</td>
                        <td>{billing.summary.inbound.totalSegments.toLocaleString()}</td>
                        <td>{formatMicros(billing.summary.inbound.costMicros, currency)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Channel breakdown</h3>
                </div>
                <div className="table-responsive">
                  <table className="table table-vcenter card-table">
                    <thead>
                      <tr>
                        <th>Channel</th>
                        <th>Messages</th>
                        <th>Segments</th>
                        <th>Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>SMS</td>
                        <td>{billing.summary.sms.messageCount.toLocaleString()}</td>
                        <td>{billing.summary.sms.totalSegments.toLocaleString()}</td>
                        <td>{formatMicros(billing.summary.sms.costMicros, currency)}</td>
                      </tr>
                      <tr>
                        <td>MMS</td>
                        <td>{billing.summary.mms.messageCount.toLocaleString()}</td>
                        <td>{billing.summary.mms.totalSegments.toLocaleString()}</td>
                        <td>{formatMicros(billing.summary.mms.costMicros, currency)}</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="row row-cards mb-3">
            <div className="col-lg-6">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Cost Per Delivery Status</h3>
                </div>
                <div className="table-responsive">
                  <table className="table table-vcenter card-table">
                    <thead>
                      <tr>
                        <th>Status</th>
                        <th>Messages</th>
                        <th>Segments</th>
                        <th>Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {statusRows.map((row) => (
                        <tr key={row.status}>
                          <td>{row.status}</td>
                          <td>{row.messageCount.toLocaleString()}</td>
                          <td>{row.totalSegments.toLocaleString()}</td>
                          <td>{formatMicros(row.costMicros, currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="col-lg-6">
              <div className="card">
                <div className="card-header">
                  <h3 className="card-title">Pricing</h3>
                </div>
                <div className="table-responsive">
                  <table className="table table-vcenter card-table">
                    <thead>
                      <tr>
                        <th>Channel</th>
                        <th>Rate (per segment)</th>
                      </tr>
                    </thead>
                    <tbody>
                      {billing.pricing.map((row) => (
                        <tr key={row.channel}>
                          <td>{row.channel}</td>
                          <td>{formatMicros(row.unitPriceMicros, row.currency)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Cost By Template</h3>
            </div>
            <div className="table-responsive">
              <table className="table table-vcenter card-table">
                <thead>
                  <tr>
                    <th>Template</th>
                    <th>Channel</th>
                    <th>Messages</th>
                    <th>Segments</th>
                    <th>Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {templateRows.map((row, index) => (
                    <tr key={`${row.templateId ?? 'none'}:${row.channel}:${index}`}>
                      <td>
                        {row.templateId ? (
                          <a
                            href={`/templates?templateKey=${encodeURIComponent(row.templateId)}`}
                            target="_blank"
                            rel="noreferrer"
                            className="fw-semibold"
                          >
                            {row.templateId}
                          </a>
                        ) : (
                          '(No template)'
                        )}
                      </td>
                      <td>{row.channel}</td>
                      <td>{row.messageCount.toLocaleString()}</td>
                      <td>{row.totalSegments.toLocaleString()}</td>
                      <td>{formatMicros(row.costMicros, currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
      {!loading && !error && billing && !hasBillingShape ? (
        <div className="alert alert-warning">
          Billing response is missing required fields. Refresh the page or contact support if this persists.
        </div>
      ) : null}
      {!loading && !error && !billing ? (
        <div className="alert alert-secondary">
          No billing data is available for the selected account and date range.
        </div>
      ) : null}
    </>
  );
}
